/**
 * 风格渲染调试模块 (module=41)
 * 训练完成后，用户在提示词调试页验证风格模型的渲染效果
 *
 * 使用 StyleRenderTrainService（@xmiles/holopix-ai）替代旧的
 * startModuleGenerate + useGenerate WebSocket 链路，
 * 同时保持 artworkStore.result / useGeneratingVersions / useTaskQueueIntegration 的对接。
 */
import type { LayoutReferState } from "@/types/image";
import type { DebugFormState } from "@/views/train/children/promptDebug/types";
import { useEventTracking } from "@/hooks/useEventTracking";
import { getClientId } from "@/hooks/useGenerate";
import { getModuleLabelById } from "@xmiles/holopix-helper";
import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { usePayStore } from "@/store/pay";
import { message, Modal } from "ant-design-vue";
import { filteringInvalidFields } from "@/utils";
import { ModuleId } from "@xmiles/holopix-types";
import { RenderAspectRatios } from "../options";
import { StyleRenderTrainService } from "@xmiles/holopix-ai";
import { useGeneratingVersions } from "@/hooks/useGeneratingVersions";
import { initGenerateResult } from "@/hooks/useGenerate";
import { useTaskQueueIntegration } from "@/hooks/usetaskQueue2";
import { $bus } from "@/mitt";
import { TEAM_QUOTA_EXCEEDED_CODE, TEAM_QUOTA_EXCEEDED_TITLE, TEAM_QUOTA_EXCEEDED_CONTENT, shouldShowTeamQuotaExceededModal } from "@/utils/teamQuotaWarning";
import { h } from "vue";
import SvgIcon from "@/components/SvgIcon/index.vue";
import { MODEL_PERMISSION_DENIED_CODE, MODEL_PERMISSION_DENIED_CONTENT } from "@/utils/modelPermissionDenied";

export const module = ModuleId.StyleRenderTrain;

export const styleRenderDebugParams = {
  module,
  quality: RenderAspectRatios.Fast,
  layoutRefer: null as null | LayoutReferState,
  redrawWeight: 3 as 1 | 2 | 3 | 4 | 5,
  colorSettings: 3,
  prompt: "",
  loraList: [] as Array<{ modelId: number; modelName?: string }>,
  selectStyle: undefined as { id: number; name: string; type: number; prompt?: string; hidePrompt?: string; url?: string; userModelId?: string } | undefined,
};

function getStyleRenderDebugGenerateParam(
  formState?: DebugFormState,
  context?: { artworkId: number }
) {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  const form = formState ?? artworkStore.styleRenderTrain.params as typeof styleRenderDebugParams;
  const { layoutRefer, quality, redrawWeight, colorSettings, prompt, loraList, selectStyle } = form;

  const params = {
    module,
    source: 1,
    userId: userStore.userInfo.id,
    artworkId: context?.artworkId ?? artworkStore.artworkState.id,
    uniqueId: userStore.userInfo.uniqueId,
    layoutRefer,
    originImgUrl: layoutRefer?.image,
    redrawWeight,
    colorSettings,
    quality,
    prompt,
    loraList,
    selectStyle,
    batchSize: 1
  } as any;

  filteringInvalidFields(params.layoutRefer, ["id", "width", "height", "image"]);

  return params;
}

export type StyleRenderDebugTask = {
  clientId: string;
  onProgress: (handler: (result: any) => void) => () => void;
  onComplete: (handler: (result: any) => void) => () => void;
  onError: (handler: (result: any) => void) => () => void;
};

export async function startStyleRenderDebugGenerate(
  formState?: DebugFormState,
  options?: {
    onClientIdGenerated?: (clientId: string) => void;
    onClientIdCleared?: () => void;
  },
  context?: { artworkId: number }
): Promise<StyleRenderDebugTask | null> {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();
  const payStore = usePayStore();
  const { set, updateResult, del } = useGeneratingVersions();

  const params = getStyleRenderDebugGenerateParam(formState, context);

  filteringInvalidFields(params.layoutRefer, ["id", "width", "height", "image"]);

  const clientId = params.clientId || params.apiClientId || getClientId();
  params.apiClientId = clientId;

  const startTime = params.startTime ?? Date.now();
  params.startTime = startTime;
  if (!params.ctime) {
    params.ctime = Date.now();
  }

  const service = new StyleRenderTrainService({
    userId: userStore.userInfo.id,
    artworkId: context?.artworkId ?? artworkStore.artworkState.id,
    uniqueId: userStore.userInfo.uniqueId
  });

  const aiParams = {
    clientId,
    prompt: params.prompt,
    loraList: params.loraList,
    originImgUrl: params.originImgUrl,
    redrawWeight: params.redrawWeight,
    colorSettings: params.colorSettings,
    quality: params.quality,
    batchSize: params.batchSize,
    vipChannel: true,
    selectStyle: params.selectStyle,
  };

  // 初始化生图状态 - 仅在非隔离模式下更新 store（兼容其他调用方）
  if (!context && artworkStore.styleRenderTrain) {
    artworkStore.styleRenderTrain.isGenerate = true;
    artworkStore.styleRenderTrain.result = { ...initGenerateResult, code: 1 };
  }

  set({
    module,
    clientId,
    params,
    result: { ...initGenerateResult, code: 1 },
    cancel: () => {}
  });
  $bus.emit("hasNewVersion");

  userStore.optimisticallyConsumeActivityRight(module, clientId);

  options?.onClientIdGenerated?.(clientId);
  reportStartEventTracking(clientId);

  const normalizeError = (error: any) => {
    const code = error?.code ?? error?.status ?? error?.data?.code ?? error?.data?.status;
    const msg = error?.msg ?? error?.message ?? error?.data?.msg ?? error?.data?.message;
    return { code, msg };
  };

  const handleGenerateError = (error: any) => {
    const { code, msg } = normalizeError(error);
    let errorMsg = msg || "生图失败，请重试";

    switch (code) {
      case TEAM_QUOTA_EXCEEDED_CODE:
        if (shouldShowTeamQuotaExceededModal()) {
          Modal.warning({
            title: TEAM_QUOTA_EXCEEDED_TITLE,
            icon: h(SvgIcon, { directory: "others", name: "tip-modal-icon", size: 24, class: "anticon" }),
            content: TEAM_QUOTA_EXCEEDED_CONTENT,
            okText: "我知道了",
            okType: "primary",
            wrapClassName: "custom-tip-modal",
            bodyStyle: { padding: "16px" },
            centered: true,
          });
        }
        return "";
      case -1:
        errorMsg = msg || "生图失败，请重试";
        break;
      case -2:
        errorMsg = msg || "生图失败，提示词含有敏感词";
        break;
      case -3:
        errorMsg = msg || "生图失败，登录已失效";
        break;
      case -100:
        errorMsg = msg || "生图失败，您的算力已不足";
        payStore.ballParams.button = "button_creation_img_not_points";
        userStore.usePayModal({ freeToast: false, powerPackage: true });
        break;
      case -200:
        errorMsg = msg || "生图失败，当前已有任务进行中，请稍后提交";
        payStore.ballParams.button = "button_creation_img_concurrent";
        userStore.usePayModal();
        break;
      case -201:
        errorMsg = msg || (userStore.showVip ? "生图失败，生图数量不符合当前套餐权益" : "生图失败");
        userStore.usePayModal();
        break;
      case -202:
        errorMsg = msg || (userStore.showVip ? "生图失败，倍数下载需vip权益" : "生图失败");
        userStore.usePayModal();
        break;
      case -203:
        errorMsg = msg || (userStore.showVip ? "生图失败，您不能使用会员权益" : "生图失败");
        userStore.usePayModal();
        break;
      case MODEL_PERMISSION_DENIED_CODE:
        errorMsg = MODEL_PERMISSION_DENIED_CONTENT;
        break;
      default:
        errorMsg = msg || "生图失败，请重试";
        break;
    }

    message.error(errorMsg);
    return errorMsg;
  };

  try {
    const integration = useTaskQueueIntegration();
    integration.handleOnWebSocketInited(clientId, module, params);

    const task = service.generate(aiParams);

    // 包装任务对象，添加 clientId 属性，使其符合 GenerationTaskLike 接口
    const wrappedTask: StyleRenderDebugTask = {
      clientId,
      onProgress: (handler) => task.onProgress(handler),
      onComplete: (handler) => task.onComplete(handler),
      onError: (handler) => task.onError(handler),
    };

    task.onProgress((result: any) => {
      // 通知任务队列进入生成阶段
      integration.handleOnGenerating(clientId);

      const updateData: any = {
        msg: result.msg || result.message || "生成中...",
        progress: Math.floor(result.progress || 0),
        code: 2,
      };
      updateResult(clientId, updateData);
    });

    task.onComplete((result: any) => {
      integration.handleOnSuccess(clientId);

      const images = result.data?.images?.map((img: any) => ({
        id: img.id,
        image: img.image,
        width: img.width || 0,
        height: img.height || 0,
        type: 0,
        collect: 0,
        size: 0,
        param: null,
        artworkId: context?.artworkId ?? artworkStore.artworkState.id,
        versionId: 0,
        artworkName: "",
        redrawWeight: null
      })) || [];

      const finalResult = {
        data: {
          status: 200,
          images: images as any,
          param: JSON.stringify(params)
        },
        msg: "生成成功",
        code: 3 as const
      };

      updateResult(clientId, finalResult);

      // 仅在非隔离模式下更新 store（兼容其他调用方）
      if (!context && artworkStore.styleRenderTrain) {
        artworkStore.styleRenderTrain.isGenerate = false;
        artworkStore.styleRenderTrain.result = { ...finalResult } as any;
      }

      $bus.emit("hasNewVersion");

      userStore.finalizeActivityRightDeduction(clientId);
      userStore.queryUserRights();
      userStore.queryActivityRights();
      const imageIds = images.map((img: any) => img.id).join(",");
      reportFinishEventTracking({ status: 200, timeCost: Date.now() - startTime, imageIds });

      options?.onClientIdCleared?.();
    });

    task.onError((err: any) => {
      const errorMsg = handleGenerateError(err);

      integration.handleOnError(clientId, errorMsg);

      del(clientId);

      // 仅在非隔离模式下更新 store（兼容其他调用方）
      if (!context && artworkStore.styleRenderTrain) {
        artworkStore.styleRenderTrain.isGenerate = false;
        artworkStore.styleRenderTrain.result = { msg: errorMsg, code: -1 } as any;
      }

      userStore.rollbackActivityRightDeduction(clientId);
      reportFinishEventTracking({ status: 500, msg: errorMsg });
      options?.onClientIdCleared?.();
    });

    // 返回包装后的任务对象，供调用方监听任务状态
    return wrappedTask;

  } catch (error: any) {
    console.error(error);
    const errorMsg = handleGenerateError(error);

    const integration = useTaskQueueIntegration();
    integration.handleOnError(clientId, errorMsg);

    del(clientId);
    // 仅在非隔离模式下更新 store（兼容其他调用方）
    if (!context && artworkStore.styleRenderTrain) {
      artworkStore.styleRenderTrain.isGenerate = false;
      artworkStore.styleRenderTrain.result = { msg: errorMsg, code: -1 } as any;
    }
    userStore.rollbackActivityRightDeduction(clientId);
    reportFinishEventTracking({ status: 500, msg: errorMsg });
    options?.onClientIdCleared?.();
    return null;
  }

  function reportStartEventTracking(clientId: string) {
    useEventTracking({
      event: "creation_event",
      eventValue: {
        client_id: clientId,
        source: "风格调试",
        module: getModuleLabelById(module) as string,
        artwork_id: (context?.artworkId ?? artworkStore.artworkState.id).toString(),
        batchSize: params.batchSize,
        resolution: params.quality,
        detail_int: params.redrawWeight
      }
    });
  }

  function reportFinishEventTracking(data: any) {
    useEventTracking({
      event: "artwork_status_event",
      eventValue: {
        module: getModuleLabelById(module) as string,
        client_id: clientId,
        source: "风格调试",
        img_id: data.imageIds || "",
        result: data.status === 200 ? 1 : 0,
        time_cost: data.timeCost,
        resolution: params.quality,
        detail_int: params.redrawWeight,
        ...(data.status !== 200 ? { fail_reason: data.msg } : {})
      }
    });
  }
}
